import { useState, useEffect, useContext } from "react";
import * as _ from "lodash";

import "../EUMRVPortStays/EUMRVPortStays.scss";

import CustomTable from "../../../../../../common/customTable/CustomTable";
import {
	EUMRV_PORTSTAYS_HEADERS,
	EuMrvPortStaysColumns,
} from "../../../../../../constants/CustomTableHeaders";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { roundOff } from "../../../../../../services/utilities";
import { FeatureType } from "../../../../../../constants/FeatureToggles";
import { ClaimsContext } from "../../../../../../services/ClaimsService";

const EmissionReported = ({ data, loading }) => {
	const [columnData, setColumndata] = useState(EuMrvPortStaysColumns);
	const staticColumns = EuMrvPortStaysColumns;
	const [showDirectMeasurements, setShowDirectMeasurements] = useState(data?.some(item => item.showDirectMeasurements));
	const { userHasFeature } = useContext(ClaimsContext) || {};
	

	// Refine the data by adding a "DieselGasOilCons" entry if not present
	useEffect(() => {
		if (
			data &&
			data[0]?.portStayConsumptions.some(
				(fuel) => fuel.fuelName === "MDO" || fuel.fuelName === "MGO"
			)
		) {
			data?.forEach((entry) => {
				if (
					entry?.portStayConsumptions.every(
						(fuel) => fuel.fuelName !== "DieselGasOilCons"
					)
				) {
					entry.portStayConsumptions.unshift({
						fuelName: "DieselGasOilCons",
						consumption: entry.dieselGasOilCons,
					});
				}
			});
		}
	}, [data]);

	// method to filter dynamic columns based on non-zero values
	const getFilteredColumns = (data, columnType, columns) => {
		const nonZeroNames = new Set();

		// Collect non-zero fuel names based on the type (consumption, MNC, transport work)
		data?.forEach((item) => {
			item[columnType]?.forEach((entry) => {
				if (
					columnType === "directCo2" &&
					((entry.reportedValue !== 0 &&
						entry.reportedValue !== 0.00 &&
						entry.reportedValue !== null &&
						entry.reportedValue !== undefined) ||
						(entry.estimatedValue !== 0 &&
							entry.estimatedValue !== 0.00 &&
							entry.estimatedValue !== null &&
							entry.estimatedValue !== undefined))
				) {
					nonZeroNames.add(entry?.reportedKey);
					nonZeroNames.add(entry?.estimatedKey);
				}
				if (
					columnType === "portStayConsumptions" &&
					entry.consumption !== 0 &&
					entry.consumption !== null &&
					entry.consumption !== undefined
				) {
					nonZeroNames.add(entry?.fuelName);
				}
				if (
					columnType === "portStayMncs" &&
					entry.mnc !== 0 &&
					entry.mnc !== null &&
					entry.mnc !== undefined
				) {
					nonZeroNames.add(entry?.fuelName);
				}
			});
		});

		// Filter columns based on non-zero values
		return columns.filter((column) => {
			const name = column.accessorKey.split("_")[1];
			return nonZeroNames.has(name);
		});
	};

	// method to filter static columns based on non-zero values
	const getFilteredStaticColumns = (objArray, staticColumns) => {
		const nonZeroHeaders = new Set();
		objArray?.forEach((obj) => {
			Object?.entries(obj)?.forEach(([key, value]) => {
				if (value !== 0 && value !== null && value !== undefined) {
					nonZeroHeaders.add(key);
				}
			});
		});
		return staticColumns.filter((column) =>
			nonZeroHeaders.has(column.accessorKey)
		);
	};

	const GetPossibleKeys = (allData, dataType, keyName) => {
		return _.chain(allData).flatMap(dataType).map(keyName).uniq().value();
	};

	const GetCo2Keys = (allData, dataType) => {
		return _.chain(allData)
			.flatMap(dataType) // Flatten all 'directCo2' arrays
			.flatMap(item => [item.reportedKey, item.estimatedKey]) // Extract both keys
			.uniq()
			.value();
	};

	const getDynamicColumns = (data) => {
		const dynamicConsColumns = [];
		const dynamicMncColumns = [];

		if (showDirectMeasurements) {
			if (Array.isArray(data[0]?.directCo2)) {
				const possibleKeys = GetCo2Keys(data, "directCo2");

				possibleKeys.forEach((item) => {
					dynamicConsColumns.push({
						id: `directCo2_${item}`,
						accessorKey: `directCo2_${item}`,
						header: `${item === "DieselGasOilCons"
							? "Diesel/Gas Oil Cons. (MT)"
							: `${item}. (MT)`
							}`,
						Cell: ({ row }) => {
							const directCo2Entry = row.original.directCo2.find(
								(fuel) => fuel.reportedKey === item || fuel.estimatedKey === item
							);
							const directCo2Value = directCo2Entry ? (item.includes('Estimated') ? directCo2Entry.estimatedValue || 0 : directCo2Entry.reportedValue || 0) : 0;

							return <div className="d-inline-flex" title={directCo2Value}>
								{roundOff(directCo2Value, 2, 2)}
								{directCo2Entry?.percentage !== '0.00' && item.includes('Estimated') && <div className="ml-2" style={{ color: directCo2Entry?.isPositive ? 'red' : 'green' }}>{directCo2Entry?.isPositive ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
									<b>{directCo2Entry?.percentage}%</b></div>}
							</div>
						},
					});
				});
			}
		}
		else {
			if (Array.isArray(data[0]?.portStayConsumptions)) {
				const possibleKeys = GetPossibleKeys(
					data,
					"portStayConsumptions",
					"fuelName"
				);
				possibleKeys?.forEach((item) => {
					dynamicConsColumns.push({
						id: `portStayConsumptions_${item}`,
						accessorKey: `portStayConsumptions_${item}`,
						header: `${item === "DieselGasOilCons"
							? "Diesel/Gas Oil Cons. (MT)"
							: `${item} Cons. (MT)`
							}`,
						Cell: ({ row }) =>
							row?.original?.portStayConsumptions.find(
								(fuel) => fuel?.fuelName === item
							)?.consumption || 0,
					});
				});
			}
		}

		if (Array.isArray(data[0]?.portStayMncs)) {
			const possibleKeys = GetPossibleKeys(
				data,
				"portStayMncs",
				"fuelName"
			);
			possibleKeys?.forEach((item) => {
				dynamicMncColumns.push({
					id: `portStayMncs_${item}`,
					accessorKey: `portStayMncs_${item}`,
					header: `${item} MNC. (MT)`,
					Cell: ({ row }) =>
						row?.original?.portStayMncs.find(
							(fuel) => fuel?.fuelName === item
						)?.mnc || 0,
				});
			});
		}
		return {
			dynamicConsColumns: getFilteredColumns(
				data,
				showDirectMeasurements ? "directCo2" : "portStayConsumptions",
				dynamicConsColumns
			),
			dynamicMncColumns: getFilteredColumns(
				data,
				"portStayMncs",
				dynamicMncColumns
			),
		};
	};

	useEffect(() => {

		const filterColumnsByIndex = (columns) => {
			 return columns;
		};

		if (loading) {
			setColumndata(filterColumnsByIndex(staticColumns));
			return;
		}

		if (data && data.length > 0) {
			const { dynamicConsColumns, dynamicMncColumns } = getDynamicColumns(data);

			const finalColumns =
				dynamicConsColumns || dynamicMncColumns
					? [
							...staticColumns.slice(0, 1),
							...getFilteredStaticColumns(data, staticColumns.slice(1, 7)),
							...dynamicConsColumns,
							...dynamicMncColumns,
							...getFilteredStaticColumns(data, staticColumns.slice(7)),
					  ]
					: getFilteredStaticColumns(data, staticColumns);

			setColumndata(filterColumnsByIndex(finalColumns));
		} else {
			setColumndata(filterColumnsByIndex(staticColumns));
		}
	}, [data, loading]);

	return (
		<div className="emission-reported mt-3 eu_mrv_ps">
			<CustomTable
				columnsData={columnData}
				renderTableData={data || []}
				customProps={{
					header: EUMRV_PORTSTAYS_HEADERS.EMISSION_REPORT,
					isLoading: loading,
					enableSort: false,
					tableName: "emissions-report",
					maxHeight: "30vh",
					columnOrder: columnData.map((column) => column.accessorKey),
				}}
			/>
		</div>
	);
};

export default EmissionReported;
